import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, IconButton } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { RxCross2 } from "react-icons/rx";
import { useGetPortfolioSummaryQuery } from "store/slices/apis/portfolioSummarySlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import Select from "react-select";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { useUpdateCommercialMutation } from "store/slices/apis/commercialTrackerApiSlice";
import moment from "moment";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditCommercial = (props) => {
    const { open, handleClose, rowData, refetchCounts } = props;
    const { projectId: routeProjectId } = useParams();
    const dispatch = useDispatch();

    const module = "Commercial"

    const { data: assetNameData } = useGetMasterDataQuery({ module: module, category: 'assetName', ProjectId: routeProjectId });
    const { data: partnerData } = useGetMasterDataQuery({ module: module, category: 'partnerName', ProjectId: routeProjectId });
    const { data: statusData } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: routeProjectId });
    const { data: revenueData } = useGetMasterDataQuery({ module: module, category: 'revenueModel', ProjectId: routeProjectId });
    const assetNameOptions =
        assetNameData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const statusOptions =
        statusData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const partnerOptions =
        partnerData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const revenueOptions =
        revenueData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const [projectType, setProjectType] = useState(localStorage.getItem("projectType") || "all")

    const { data, error, isLoading, isFetching: isProjectFething, refetch } = useGetAllProjectsQuery(projectType);
    const { data: portfolioData, isFetching: portfolioLoader, isError: portfolioError } = useGetPortfolioSummaryQuery();

    const projectData = data?.data || [];
    const filteredProjectData = projectData.filter((data) => data.active == true);

    const projectOptions = (filteredProjectData || []).map(p => ({
        value: p.projectId,
        label: p.projectName,
        projectType: p.projectType,
    }));

    const portfolioOptions = (portfolioData?.data || []).map(p => ({
        value: p.portfolioName,
        label: p.portfolioName,
        projectType: p.portFolioType,
    }));

    const portfolioSelectOptions = useMemo(
        () => [
            // { projectType: "all", label: "All Portfolio" },
            ...portfolioOptions,
        ],
        [portfolioOptions]
    );

    const handlePortfolioChange = (option, setFieldValue) => {
        let projectType = option?.projectType || "all";
        setProjectType(projectType);
        localStorage.setItem("projectType", projectType)
        setFieldValue("product", option?.value || "");
    };

    const [updateCommercial] = useUpdateCommercialMutation();

    const [initialFormData, setInitialFormData] = useState({
        assetType: "",
        assetName: "",
        status: "",
        partnerOrTenantName: "",
        revenueModel: "",
        area: "",
        lcd: "",
        product: "",
        project: "",
        projectId: ""
    });

    useEffect(() => {
        if (rowData && open) {
            setInitialFormData({
                assetType: rowData.assetType || "",
                assetName: rowData.assetName || "",
                status: rowData.status || "",
                revenueModel: rowData.revenueModel || "",
                area: rowData.area || "",
                partnerOrTenantName: rowData.partnerOrTenantName || "",
                product: rowData?.product || "",
                project: rowData?.project || "",
                projectId: rowData?.projectID || "",
                lcd: rowData?.lcd
                    ? moment.utc(rowData.lcd).format("YYYY-MM-DD")
                    : null,
                // financialId: rowData.financialId || null,
            });
        }
    }, [rowData, open, routeProjectId]);

    const validationSchema = Yup.object({
        product: Yup.string().required("Product is required"),
        project: Yup.string().required("Project is required"),
        assetType: Yup.string().trim().required("Asset Type is required"),
        assetName: Yup.string().required("Asset Name is required"),
        area: Yup.number().typeError("Must be a number").positive("Must be positive"),
        // component: Yup.string()
        //     .required("Component is required"),

        // status: Yup.string()
        //     .required("Status is required"),

        // partnerName: Yup.string()
        //     .required("Partner Name is required"),

        // revenueModel: Yup.string()
        //     .required("Revenue Model is required"),

        // area: Yup.string(),

        // lcd: Yup.date()
        //     .typeError("Invalid date")
        //     .required("LCD (Date) is required"),
    });

    const stringToDecimal = (value) => {
        if (value === "" || value === null || value === undefined) return null;

        const num = Number(value);
        if (isNaN(num)) return null;

        return Number(num.toFixed(2));
    };

    const emptyGUID = "00000000-0000-0000-0000-000000000000";

    const handleSubmit = async (values, { setSubmitting }) => {
        const reqBody = {
            projectId: values?.projectId || "",
            commercialAssetId: rowData?.commercialAssetId,
            assetType: values?.assetType,
            assetName: values?.assetName,
            status: values?.status,
            partnerOrTenantName: values?.partnerOrTenantName,
            revenueModel: values?.revenueModel,
            area: values?.area ? stringToDecimal(values?.area) : 0,
            lcd: values?.lcd
                ? moment.utc(values?.lcd, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            product: values?.product,
            project: values?.project
        };

        // console.log("Update payload:", reqBody);

        try {
            const response = await updateCommercial({ body: reqBody }).unwrap();
            openNotification(response.message || "Updated successfully", "success");
            setSubmitting(false);
            if (refetchCounts) refetchCounts();
            handleClose();
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: { color: type },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (

        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden",
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "#ECECEC",
                    pb: 2,
                    pt: 3,
                    width: "100%",
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "27px",
                            lineHeight: "100%",
                            color: "#140F36",
                            fontFamily: "Outfit, sans-serif",
                        }}
                    >
                        Edit Commercial & others
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialFormData}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, submitCount, handleBlur, setFieldTouched, setFieldValue, handleSubmit, isSubmitting }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                {routeProjectId == undefined &&
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>
                                            Product
                                        </Typography>

                                        <Select
                                            name="product"
                                            options={portfolioSelectOptions}
                                            value={
                                                portfolioSelectOptions.find(
                                                    (opt) => opt.value === values.product
                                                ) || null
                                            }
                                            onChange={(option) => handlePortfolioChange(option, setFieldValue)}
                                            onBlur={() => setFieldTouched("product", true)}
                                            placeholder="Select Portfolio"
                                        />

                                        {(touched.product || submitCount > 0) && errors.product && (
                                            <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                                                {errors.product}
                                            </Typography>
                                        )}
                                    </Grid>
                                }

                                {routeProjectId == undefined &&
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>
                                            Project
                                        </Typography>

                                        <Select
                                            name="project"
                                            options={[{ value: emptyGUID, label: "All Projects" }, ...projectOptions]}
                                            value={
                                                [{ value: emptyGUID, label: "All Projects" }, ...projectOptions]
                                                    .find(opt => opt.value === values.projectId) || null
                                            }
                                            onChange={(option) => {
                                                setFieldValue("projectId", option?.value);
                                                setFieldValue("project", option?.label);
                                            }}
                                            onBlur={() => setFieldTouched("project", true)}
                                            placeholder="Select Project"
                                        />

                                        {(touched.project || submitCount > 0) && errors.project && (
                                            <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                                                {errors.project}
                                            </Typography>
                                        )}
                                    </Grid>
                                }

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Asset Type
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="assetType"
                                        type="textField"
                                        value={values.assetType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Asset Type"
                                        InputProps={{
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#5F5F5F",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    "&:-webkit-autofill": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:hover": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:focus": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    {touched.assetType && errors.assetType && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.assetType}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Asset Name
                                    </Typography>
                                    <Select
                                        name="assetName"
                                        options={assetNameOptions}
                                        value={assetNameOptions.find(opt => opt.value === values.assetName) || null}
                                        onChange={(option) => setFieldValue("assetName", option.value)}
                                        onBlur={() => setFieldTouched("assetName", true)}
                                        placeholder="Select Asset Name"
                                    />
                                    {touched.assetName && errors.assetName && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.assetName}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Status
                                    </Typography>
                                    <Select
                                        name="status"
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === String(values.status)) || null}
                                        onChange={(option) => setFieldValue("status", option.value)}
                                        onBlur={() => setFieldTouched("status", true)}
                                        placeholder="Select Status"
                                    />
                                    {touched.status &&
                                        errors.status && (
                                            <Typography
                                                sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}
                                            >
                                                {errors.status}
                                            </Typography>
                                        )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Partner Name
                                    </Typography>
                                    <Select
                                        name="partnerOrTenantName"
                                        options={partnerOptions}
                                        value={partnerOptions.find(opt => opt.value === values.partnerOrTenantName) || null}
                                        onChange={(option) => setFieldValue("partnerOrTenantName", option.value)}
                                        onBlur={() => setFieldTouched("partnerOrTenantName", true)}
                                        placeholder="Select Partner"
                                    />
                                    {touched.partnerOrTenantName && errors.partnerOrTenantName && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.partnerOrTenantName}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Revenue Model
                                    </Typography>
                                    <Select
                                        name="revenueModel"
                                        options={revenueOptions}
                                        value={revenueOptions.find(opt => opt.value === values.revenueModel) || null}
                                        onChange={(option) => setFieldValue("revenueModel", option.value)}
                                        onBlur={() => setFieldTouched("revenueModel", true)}
                                        placeholder="Select Revenue Model"
                                    />
                                    {touched.revenueModel && errors.revenueModel && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.revenueModel}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        Area
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="area"
                                        value={values.area}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Area"
                                        InputProps={{
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#5F5F5F",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    "&:-webkit-autofill": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:hover": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:focus": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    {touched.area && errors.area && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.area}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>
                                        LCD
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="lcd"
                                        type="date"
                                        value={values.lcd}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter lcd"
                                        InputProps={{
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#5F5F5F",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.lcd && errors.lcd && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.lcd}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Action Buttons */}
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            alignItems: "center",
                                            gap: 2,
                                            mt: 2,
                                        }}
                                    >
                                        <Button onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                borderRadius: "25px",
                                                fontWeight: 500,
                                                px: "20px",
                                                py: "15px",
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={isSubmitting}
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "20px",
                                                py: "15px",
                                                '&:disabled': {
                                                    backgroundColor: '#cccccc',
                                                }
                                            }}
                                        >
                                            {isSubmitting ? "Updating..." : "Update"}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>

    );
};

export default EditCommercial;